import { IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { ICanvasScaler } from "../../../../../../../canvas/ICanvasScaler";
import { ExternalWalletType } from "../../../../../../../web3/externalWallets/ExternalWalletType";
import { BasePageView } from "../../../../../common/components/BasePageView";
import { CommonResourcesPackage } from "../../../../../common/resources/CommonResourcesPackage";
import { IWalletConnectPage } from "./IWalletConnectPage";
import gsap from "gsap";
import { GameFont } from "../../../../../common/GameFont";

export class SuccessWalletConnect extends BasePageView implements IWalletConnectPage {
    private static readonly HEADER_TEXT = "SUCCESSFULLY";
    private static readonly INFO_TEXT = "YOUR {0} WALLET\nHAS BEEN CONNECTED";

    private readonly commonResources: CommonResourcesPackage;
    private readonly walletType: ExternalWalletType;
    private readonly showTimeline: gsap.core.Timeline;

    private leftGlow: Sprite;
    private rightGlow: Sprite;
    private headerText: Text;
    private infoText: Text;

    public constructor(
        commonResources: CommonResourcesPackage,
        walletType: ExternalWalletType,
        canvasScaler: ICanvasScaler
    ) {
        super(canvasScaler);
        this.commonResources = commonResources;
        this.walletType = walletType;
        this.build();
        this.showTimeline = new gsap.core.Timeline({ paused: true });
    }

    public async show(): Promise<void> {
        this.showTimeline.fromTo(this.leftGlow, { alpha: 0 }, { alpha: 1, duration: 0.4 }, "<");
        this.showTimeline.fromTo(this.rightGlow, { alpha: 0 }, { alpha: 1, duration: 0.4 }, "<");
        this.showTimeline.fromTo(this.headerText, { alpha: 0 }, { alpha: 1, duration: 0.4 }, "<");
        this.showTimeline.fromTo(this.infoText, { alpha: 0 }, { alpha: 1, duration: 0.4 }, "<");
        await this.showTimeline.play();
    }

    public async hide(): Promise<void> {
        await this.showTimeline.reverse();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.showTimeline.kill();
        super.destroy(options);
    }

    protected override build(): void {
        this.leftGlow = new Sprite(this.commonResources.assets_common_ui_lights_light_left_png);
        this.addChild(this.leftGlow);

        this.rightGlow = new Sprite(this.commonResources.assets_common_ui_lights_light_left_png);
        this.rightGlow.scale.x = -1;
        this.addChild(this.rightGlow);

        this.headerText = new Text(SuccessWalletConnect.HEADER_TEXT, new HeaderTextStyle());
        this.headerText.skew.x = -0.08;
        this.headerText.angle = -5;
        this.addChild(this.headerText);

        this.infoText = new Text(SuccessWalletConnect.INFO_TEXT.replace("{0}", this.walletType.toUpperCase()), new InfoTextStyle());
        this.addChild(this.infoText);

        super.build();
    }

    protected override layout(width: number, _height: number): void {
        this.leftGlow.position.set(-12, 651);
        this.rightGlow.position.set(width + 12, 651);
        this.infoText.position.set((width - this.infoText.width) * 0.5, 758);
        this.headerText.position.set((width - this.headerText.width) * 0.5, this.infoText.position.y + this.infoText.height + 60);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super({
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 144,
            lineHeight: 129,
            fillGradientType: 1,
            fill: [
                "#AAFD09",
                "#16CF15"
            ],
            dropShadow: true,
            dropShadowAlpha: 0.3,
            dropShadowAngle: 0.5,
            dropShadowColor: "#0d0606"
        });
    }
}

class InfoTextStyle extends TextStyle {
    public constructor() {
        super({
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 48,
            lineHeight: 56,
            fill: 0xffffff
        });
    }
}